import { computed, ComputedRef, Ref, ref, watch } from 'vue'

import { HTMLAudioElementWithSetSinkId } from '@/entities/media-devices'
import { useWebRTC, STATE, RTCSessionFacade } from '@/entities/webrtc'

interface Result {
  liveCallSessions: ComputedRef<RTCSessionFacade[]>
  processSessions: ComputedRef<RTCSessionFacade[]>
  pendingSessions: ComputedRef<RTCSessionFacade[]>
  player: Ref<HTMLAudioElementWithSetSinkId | undefined>
  currentSession: ComputedRef<RTCSessionFacade | undefined>
}

const player = ref<HTMLAudioElementWithSetSinkId>()

export const useLiveCallSessions = (): Result => {
  const { sessions, activeSessionId } = useWebRTC()

  const liveCallSessions = computed(() => {
    return Array.from(sessions.value.values())
  })

  const processSessions = computed(() => liveCallSessions.value.filter((session) => {
    const st = session.sessionState.value
    return st === STATE.CONNECTED || st === STATE.ONHOLD
  }))

  const pendingSessions = computed(() => liveCallSessions.value.filter((session) => {
    const st = session.sessionState.value
    return st === STATE.RINGING || st === STATE.PROGRESS
  }))

  const currentSession = computed(() => {
    if (!activeSessionId.value) return undefined
    return sessions.value.get(activeSessionId.value)
  })

  // Call audio plays per-participant media elements — keep global player empty (no mixer / sink).
  watch(player, (newPlayer) => {
    if (newPlayer) {
      newPlayer.srcObject = null
    }
  })

  return {
    liveCallSessions,
    processSessions,
    pendingSessions,
    player,
    currentSession,
  }
}
